<script setup>
import CreateCustomerAuditAction from '@frontJs/components/actions/projectManagement/sem/CreateCustomerAuditAction.vue';
import FileTextIcon from '@iconify-vue/ph/file-text';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import DisplayContentAction from '@js/components/actions/DisplayContentAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { tenant, all } = defineProps({
    tenant: {
        type: Object,
        required: false,
    },
    all: {
        type: Boolean,
        required: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
    globalData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'tenant.name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Client',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.tenant.name,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'id',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'ID',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.id,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'websiteVariant',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Site',
                }),
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.websiteVariant.name,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                props.row.original.name
                    ? h(TextCell, {
                          text: props.row.original.name,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'users',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Utilisateurs',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.users
                        .map((user) => user.fullName)
                        .join(', '),
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'date',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Date',
                }),
            cell: (props) =>
                props.row.original.date
                    ? h(TextCell, {
                          text: props.row.original.date,
                      })
                    : null,
        }),
        columnHelper.display({
            id: 'details',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Détails',
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(
                                DisplayContentAction,
                                {
                                    title: 'Notes',
                                    content: props.row.original.notes,
                                },
                                {
                                    trigger: () =>
                                        h(ActionButton, {
                                            size: 'small',
                                            square: true,
                                            title: 'Voir les notes',
                                            icon: FileTextIcon,
                                            type: 'primary',
                                        }),
                                },
                            ),
                        ],
                    },
                ),
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                size: 'small',
                                title: 'Modifier',
                                href: route(
                                    'front.projectManagement.sem.customerAudit',
                                    {
                                        tenant: tenant
                                            ? tenant.id
                                            : props.row.original.tenant.id,
                                        customerAudit: props.row.original.id,
                                    },
                                ),
                                icon: PencilSimpleIcon,
                                square: true,
                            }),
                        ],
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.sem.postCustomerAudits', {
        tenant: tenant ? tenant.id : null,
        all,
    }),
    columnPinning: {
        right: ['actions'],
    },
    columnVisibility: {
        'tenant.name': !tenant,
        id: !!tenant,
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    >
        <template #actions>
            <ActionButton
                v-if="tenant"
                size="small"
                :href="
                    route('front.projectManagement.sem.customerAudit', {
                        tenant: tenant.id,
                    })
                "
                :left-icon="PlusIcon"
            >
                Ajouter
            </ActionButton>

            <CreateCustomerAuditAction v-else :tenants="globalData.tenants">
                <template #trigger>
                    <ActionButton
                        size="small"
                        :left-icon="PlusIcon"
                        type="primary"
                    >
                        Ajouter
                    </ActionButton>
                </template>
            </CreateCustomerAuditAction>
        </template>
    </DataTableLayout>
</template>
